import { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem as MuiMenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { MenuItem, MenuSection } from "src/business/restaurant/menu";
import { CustomButton } from "src/design";
import { colorTextMuted, radiusInput } from "src/design/tokens";
import { useTranslation } from "react-i18next";

export interface MenuItemFormData {
  name: string;
  category: string;
  description: string;
  price: string;
  active: boolean;
  existingId?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  sections: MenuSection[];
  onSubmit: (data: MenuItemFormData) => Promise<void>;
  loading: boolean;
  initialData?: {
    item: MenuItem;
    sectionName: string;
  };
}

export const AddMenuItemModal = ({
  open,
  onClose,
  sections,
  onSubmit,
  loading,
  initialData,
}: Props) => {
  const { t } = useTranslation();
  const isEdit = Boolean(initialData);

  const defaultCategory = sections[0]?.section ?? "";

  const [name, setName] = useState(initialData?.item.name ?? "");
  const [category, setCategory] = useState(initialData?.sectionName ?? defaultCategory);
  const [description, setDescription] = useState(initialData?.item.description ?? "");
  const [price, setPrice] = useState(initialData?.item.price ?? "");
  const [status, setStatus] = useState<"active" | "inactive">("active");

  useEffect(() => {
    if (open) {
      setName(initialData?.item.name ?? "");
      setCategory(initialData?.sectionName ?? defaultCategory);
      setDescription(initialData?.item.description ?? "");
      setPrice(initialData?.item.price ?? "");
      setStatus("active");
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!name.trim() || !price) return;
    await onSubmit({
      name: name.trim(),
      category,
      description,
      price,
      active: status === "active",
      existingId: initialData?.item.id ?? undefined,
    });
  };

  const isValid = name.trim().length > 0 && price.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.125rem" }}>
              {isEdit ? t("menu.modal.item.editTitle") : t("menu.modal.item.title")}
            </Typography>
            <Typography sx={{ fontSize: "0.875rem", color: colorTextMuted, mt: 0.25 }}>
              {t("menu.modal.item.subtitle")}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ mt: -0.5 }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, mb: 0.5 }}>
              {t("menu.modal.item.name")} *
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: radiusInput } }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, mb: 0.5 }}>
              {t("menu.modal.item.category")}
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                displayEmpty
                sx={{ borderRadius: radiusInput }}
              >
                {sections.map((s) => (
                  <MuiMenuItem key={s.section} value={s.section}>
                    {s.section}
                  </MuiMenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, mb: 0.5 }}>
              {t("menu.modal.item.description")}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("menu.modal.item.descriptionPlaceholder")}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: radiusInput } }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Box>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, mb: 0.5 }}>
                {t("menu.modal.item.price")} *
              </Typography>
              <TextField
                size="small"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                type="number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ fontSize: "0.875rem" }}>$</Typography>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography sx={{ fontSize: "0.75rem", color: colorTextMuted }}>MAD</Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: radiusInput } }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, mb: 0.5 }}>
                {t("menu.modal.item.status")}
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={status}
                onChange={(_, v) => { if (v) setStatus(v); }}
                size="small"
              >
                <ToggleButton value="active" sx={{ textTransform: "none", fontSize: "0.8125rem", px: 2 }}>
                  {t("menu.modal.item.active")}
                </ToggleButton>
                <ToggleButton value="inactive" sx={{ textTransform: "none", fontSize: "0.8125rem", px: 2 }}>
                  {t("menu.modal.item.inactive")}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <CustomButton color="inverted" onClick={onClose}>
          {t("common.cancel")}
        </CustomButton>
        <CustomButton
          color="black"
          onClick={handleSubmit}
          disabled={!isValid || loading}
          disabledColor="rgba(255,255,255,0.4)"
        >
          {isEdit ? t("menu.modal.item.saveButton") : t("menu.modal.item.addButton")}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};
